<template>
  <div v-if="shouldDisplay" ref="banner" class="information-header" role="status" :style="bannerStyle">
    <p class="information-header__text">
      <FontAwesomeIcon v-if="iconClass" :icon="iconClass" class="information-header__icon" aria-hidden="true" />
      {{ informationHeader.text }}
    </p>
  </div>
</template>

<script>
import { mapState } from "pinia";
import { usePlatformStore } from "@/store/platform/platform";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";

const CSS_HEIGHT_VARIABLE = "--information-header-height";

export default {
  name: "InformationHeaderComponent",
  components: {
    FontAwesomeIcon,
  },
  data() {
    return {
      resizeObserver: null,
    };
  },
  computed: {
    ...mapState(usePlatformStore, {
      design: (store) => store.design,
    }),

    informationHeader() {
      return this.design && this.design.informationHeader ? this.design.informationHeader : null;
    },

    shouldDisplay() {
      return Boolean(this.informationHeader && this.informationHeader.text);
    },

    iconClass() {
      // Le préfixe FontAwesome est fixé côté front ; le back ne stocke que le nom (ex : circle-info).
      return this.informationHeader && this.informationHeader.icon
        ? `fa-light fa-${this.informationHeader.icon}`
        : null;
    },

    bannerStyle() {
      if (!this.shouldDisplay) {
        return {};
      }

      const backgroundColor = this.informationHeader.color || "#000000";

      return {
        backgroundColor,
        color: this.resolveTextColor(backgroundColor),
      };
    },
  },
  watch: {
    shouldDisplay: {
      immediate: true,
      handler() {
        this.$nextTick(() => {
          this.observeBanner();
          this.updateHeightVariable();
        });
      },
    },
  },
  mounted() {
    if (typeof ResizeObserver !== "undefined") {
      this.resizeObserver = new ResizeObserver(() => this.updateHeightVariable());
    }
    this.observeBanner();
    this.updateHeightVariable();
  },
  beforeUnmount() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    this.setHeightVariable(0);
  },
  methods: {
    /**
     * Observe la hauteur du bandeau pour réagir aux retours à la ligne
     * (texte long, redimensionnement de la fenêtre).
     */
    observeBanner() {
      if (this.resizeObserver && this.$refs.banner) {
        this.resizeObserver.disconnect();
        this.resizeObserver.observe(this.$refs.banner);
      }
    },

    /**
     * Pousse le header fixe et le contenu vers le bas en exposant la hauteur
     * du bandeau dans une variable CSS globale (0 quand il est masqué).
     */
    updateHeightVariable() {
      const height = this.shouldDisplay && this.$refs.banner ? this.$refs.banner.offsetHeight : 0;
      this.setHeightVariable(height);
    },

    setHeightVariable(height) {
      document.documentElement.style.setProperty(CSS_HEIGHT_VARIABLE, `${height}px`);
    },

    /**
     * Retourne la couleur de texte à appliquer : la couleur configurée par
     * l'admin si elle offre un contraste suffisant (RGAA AA, ratio >= 4.5:1),
     * sinon un repli noir/blanc lisible calculé sur la couleur de fond.
     */
    resolveTextColor(backgroundColor) {
      const configured = this.informationHeader.textColor;

      if (configured && this.getContrastRatio(configured, backgroundColor) >= 4.5) {
        return configured;
      }

      return this.getReadableTextColor(backgroundColor);
    },

    /**
     * Ratio de contraste WCAG entre deux couleurs (0 si une couleur est invalide).
     */
    getContrastRatio(hexA, hexB) {
      const luminanceA = this.getRelativeLuminance(hexA);
      const luminanceB = this.getRelativeLuminance(hexB);

      if (luminanceA === null || luminanceB === null) {
        return 0;
      }

      const lighter = Math.max(luminanceA, luminanceB);
      const darker = Math.min(luminanceA, luminanceB);

      return (lighter + 0.05) / (darker + 0.05);
    },

    /**
     * Choisit noir ou blanc pour le texte en fonction de la couleur de fond,
     * en retenant la couleur offrant le meilleur ratio de contraste WCAG
     * afin de garantir la lisibilité (RGAA, critères 3.2/3.3).
     */
    getReadableTextColor(hexColor) {
      const backgroundLuminance = this.getRelativeLuminance(hexColor);

      if (backgroundLuminance === null) {
        return "#ffffff";
      }

      const contrastWithBlack = (backgroundLuminance + 0.05) / 0.05;
      const contrastWithWhite = 1.05 / (backgroundLuminance + 0.05);

      return contrastWithBlack >= contrastWithWhite ? "#000000" : "#ffffff";
    },

    /**
     * Calcule la luminance relative d'une couleur hexadécimale selon WCAG 2.1.
     * Retourne null si la couleur n'est pas un hexadécimal long (#rrggbb).
     */
    getRelativeLuminance(hexColor) {
      let hex = hexColor.replace("#", "");

      if (hex.length === 3) {
        hex = hex
          .split("")
          .map((char) => char + char)
          .join("");
      }

      if (hex.length !== 6) {
        return null;
      }

      const channels = [hex.slice(0, 2), hex.slice(2, 4), hex.slice(4, 6)].map((channel) => {
        const value = parseInt(channel, 16) / 255;

        return value <= 0.03928 ? value / 12.92 : Math.pow((value + 0.055) / 1.055, 2.4);
      });

      return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
    },
  },
};
</script>

<style lang="scss" scoped>
.information-header {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  padding: 2px 20px;
  text-align: center;
  z-index: 1040;

  &__text {
    margin: 0;
    font-size: 13px;
    line-height: 1.3;
    font-weight: 600;
  }

  &__icon {
    margin-right: 6px;
  }
}
</style>
